import { Feature } from "ol";
import VectorSource from "ol/source/Vector";
import { useContext, useEffect, useMemo, useState } from "react";
import MapContext from "./MapContext";
import { VECTOR_LAYERS } from "./MapView";
import Style from "ol/style/Style";
import { Geometry, Point } from "ol/geom";
import Icon from "ol/style/Icon";

import { renderToStaticMarkup } from "react-dom/server";
import { StateColors } from "../../pages/InspectionList/StateHelpers";
import { InspectionfieldStateEnum } from "./__generated__/MapListViewFragment.graphql";

function MapMarker({
  id,
  point,
  state,
  selected,
  focused,
}: {
  id?: string;
  point: number[];
  state?: InspectionfieldStateEnum | null;
  selected?: boolean;
  focused?: boolean;
}) {
  const { map } = useContext(MapContext);
  const [feature, setFeature] = useState<Feature<Geometry>>();
  useEffect(() => {
    const geom = new Point(point);
    const feature = new Feature({
      type: "icon",
      geometry: geom,
    });
    feature?.getGeometry()?.transform("EPSG:4326", "EPSG:3857");
    const layer = map
      ?.getAllLayers()
      ?.find((l) => l.getProperties()?.name === VECTOR_LAYERS.FIELDS_MARKERS);
    const source = layer?.getSource() as VectorSource | undefined;
    feature && source?.addFeature(feature);
    feature && setFeature(feature);
    feature && id && feature.setProperties({ id });
    return () => {
      feature && source?.removeFeature(feature);
      setFeature(undefined);
    };
  }, [map, point, id]);
  const icon = useMemo(
    () =>
      svgToDataURL(
        <Marker
          fillColor={(state && StateColors[state]) || "#0075BC"}
          selected={selected}
          shadowOpacity={focused ? 1 : 0.2}
          isFocused={focused}
        />
      ),
    [state, selected, focused]
  );
  useEffect(() => {
    let iconStyle: Style[] | Style = new Style({
      image: new Icon({
        width: 65,
        height: 72,
        anchor: [0.5, 0.65],
        anchorXUnits: "fraction",
        anchorYUnits: "fraction",
        declutterMode: "obstacle",
        src: icon,
      }),
    });
    feature?.setStyle(iconStyle);
    return () => feature?.setStyle(undefined);
  }, [feature, icon]);
  return null;
}

MapMarker.defaultProps = {
  state: "assigned",
};

const svgToDataURL = (svgComponent: any) => {
  const markup = renderToStaticMarkup(svgComponent);
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(markup)}`;
};

const Marker = ({
  fillColor = "#0075BC",
  borderColor = undefined,
  selected = false,
  shadowOpacity = 0.2,
  isFocused = false,
}: {
  fillColor: string;
  borderColor?: string;
  selected?: boolean;
  shadowOpacity?: number;
  isFocused?: boolean;
}) => (
  <svg
    width="65"
    height="72"
    viewBox="0 0 65 72"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <filter id="drop-shadow" x="-50%" y="-50%" width="200%" height="200%">
      <feDropShadow
        dx="3"
        dy="10"
        stdDeviation={isFocused ? 6 : 2}
        floodColor={`rgba(0, 0, 0, ${shadowOpacity})`}
      />
    </filter>

    <g filter="url(#drop-shadow)">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M36.106 38.5514C39.0117 37.8392 41.7664 36.3507 44.0405 34.0853C50.692 27.459 50.7125 16.6952 44.0863 10.0437C37.46 3.39215 26.6962 3.37164 20.0447 9.99789C13.3931 16.6241 13.3726 27.3879 19.9989 34.0395C22.2291 36.2782 24.928 37.7658 27.7811 38.5015L31.9954 46.0839L36.106 38.5514Z"
        fill={fillColor}
        stroke={borderColor}
        strokeWidth={4}
      />
      <circle cx="32" cy="22" r={6} fill="white" />

      {selected ? (
        <path
          width="12"
          height="12"
          d="M5.8432 10.4969L2.9312 7.32408C2.75625 7.13346 2.75625 6.8244 2.9312 6.63376L3.56475 5.94345C3.7397 5.75281 4.02337 5.75281 4.19832 5.94345L6.15999 8.08078L10.3617 3.50283C10.5366 3.31221 10.8203 3.31221 10.9952 3.50283L11.6288 4.19314C11.8037 4.38376 11.8037 4.69282 11.6288 4.88345L6.47677 10.4969C6.30181 10.6875 6.01815 10.6875 5.8432 10.4969Z"
          fill="#212529"
          transform="translate(24.5 15)"
        />
      ) : null}

      {isFocused && (
        <circle
          cx="32"
          cy="6"
          r="4"
          fill="#FF4136"
          stroke="white"
          strokeWidth=".5"
        />
      )}
    </g>
  </svg>
);

export const StateToIcon: { [key: string]: string } = {
  ASSIGNED: svgToDataURL(<Marker fillColor={"#0075BC"} />),
  PLANNED: svgToDataURL(<Marker fillColor={"#9D56E4"} />),
  IN_PROGRESS: svgToDataURL(<Marker fillColor={"#F9BF2B"} />),
};

export default MapMarker;
